import {
  InvalidParamError,
  getActiveNetworkData,
} from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  getDefaultClient,
  getWalletProviderFromWalletAccount,
} from '@dynamic-labs-sdk/client/core';
import type {
  Account,
  Chain,
  Hex,
  HttpTransportConfig,
  Transport,
  WalletClient,
  WalletClientConfig,
} from 'viem';
import { createWalletClient, custom } from 'viem';
import { toAccount } from 'viem/accounts';

import type { EvmWalletAccount } from '../../EvmWalletAccount.types';
import { isEvmWalletProvider } from '../../isEvmWalletProvider';
import { isEIP1193Provider } from '../../utils/isEIP1193Provider';
import { mapNetworkDataToViemChain } from '../utils/mapNetworkDataToViemChain';
import { isEvmWalletProviderWithCustomViemClient } from '../utils/walletProviderHasCustomWalletClient';

type CreateWalletClientForWalletAccountProps = {
  httpTransportConfig?: HttpTransportConfig;
  walletAccount: EvmWalletAccount;
  walletClientConfig?: Omit<
    WalletClientConfig,
    'account' | 'chain' | 'transport'
  >;
};

/**
 * Creates a viem WalletClient from an EVM WalletAccount object.
 *
 * This function configures a viem WalletClient instance for sending transactions
 * and signing messages using the specified wallet account. It automatically
 * handles network configuration and transport setup.
 *
 * @param props - The configuration object.
 * @param props.walletAccount - The EVM wallet account to create the client for.
 * @param [props.walletClientConfig] - Optional additional configuration for the viem WalletClient.
 * @param [props.httpTransportConfig] - Optional HTTP transport configuration.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to a configured viem WalletClient instance.
  * @not-instrumented
 */
export const createWalletClientForWalletAccount = async (
  {
    walletAccount,
    walletClientConfig = {},
    httpTransportConfig = {},
  }: CreateWalletClientForWalletAccountProps,
  client = getDefaultClient()
): Promise<WalletClient<Transport, Chain, Account>> => {
  const walletProvider = getWalletProviderFromWalletAccount(
    { walletAccount },
    client
  );

  if (!isEvmWalletProvider(walletProvider)) {
    throw new InvalidParamError(
      `${walletAccount.address} is not an EVM wallet account`
    );
  }

  if (isEvmWalletProviderWithCustomViemClient(walletProvider)) {
    return walletProvider.createViemWalletClient({
      httpTransportConfig,
      viemWalletClientConfig: walletClientConfig,
      walletAccount,
    });
  }

  const { networkData: activeNetworkData } = await getActiveNetworkData(
    { walletAccount },
    client
  );

  assertDefined(
    activeNetworkData,
    `No network data found for wallet account ${walletAccount.address}`
  );

  if (!isEIP1193Provider(walletProvider)) {
    throw new InvalidParamError(
      'Wallet provider does not implement the EIP1193Provider interface'
    );
  }

  return createWalletClient({
    account: toAccount(walletAccount.address as Hex),
    chain: mapNetworkDataToViemChain(activeNetworkData),
    transport: custom(walletProvider, httpTransportConfig),
    ...walletClientConfig,
  });
};
